import React, { Component, ReactNode } from 'react';
import { KompisText, KompisMediaCarousel } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import CoverPickerDescriptionLightbox from './CoverProductDescription/CoverProductDescriptionLightbox';
import {
	CoverItem,
	ImageItem,
	SofaRangeName,
	Platform,
	DeviceType,
	Orientation,
} from 'sofa-reborn-react';
import { localeService } from '../../services';
import {
	StyledSpanWrapper,
	EmptySpanWrapper,
	StyledSpan,
	CoversWrapper,
	StyledCover,
	StyledNameIcon,
	MediaWrapper,
	MissingCoverWrapper,
	CarouselContainer,
	StyledCoverHeader,
	SkapaButtonWrapper,
} from './CoverPickerStyle';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import SkapaButton from '@ingka/button';
import SkapaModal, {
	Sheets as SkapaSheets,
	ModalHeader as SkapaModalHeader,
	ModalBody as SkapaModalBody,
	ModalFooter as SkapaModalFooter,
} from '@ingka/modal';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';

type Props = {
	images: ImageItem[];
	covers: CoverItem[];
	nrOfEmptyElements: number;
	replaceCovers: CoverItem[];
	selectedCover: CoverItem;
	selectedIndex?: number;
	sofaRangeName: SofaRangeName;
	missingCover: boolean;
	showSheet: boolean;
	productDescriptionOpen?: boolean;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	onProductDescriptionExpand?: () => void;
	onClick?: () => void;
	onCoverClick?: (coverItem: CoverItem) => void;
	onChange?: (selectedIndex: number) => void;
};

/**
 * React component.
 *
 */
export default class MissingCoverPicker extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;
		const { onClick, onChange, onCoverClick, onProductDescriptionExpand } = props;

		const covers = props.missingCover ? props.replaceCovers : props.covers;
		const coverElements = covers.map(function (cover) {
			const active = cover.articleId === props.selectedCover.articleId ? 'active' : '';
			return (
				<StyledSpanWrapper
					platform={props.platform}
					key={cover.articleId}
					onClick={(): void => onCoverClick && onCoverClick(cover)}
					onKeyDown={(e): void => {
						if (e.key === 'Enter') {
							onCoverClick && onCoverClick(cover);
						}
					}}
					role="button"
					aria-describedby={cover.articleId}
					tabIndex={0}
					style={{
						borderRadius: '50%',
					}}
				>
					<StyledSpan
						className={active}
						platform={props.platform}
						color={cover.color}
						borderColor={cover.borderColor as string}
					>
						<div id={cover.articleId} style={{ display: 'none' }}>
							{cover.name}
						</div>
					</StyledSpan>
				</StyledSpanWrapper>
			);
		});

		/* Create empty wrapper to always get four on each row for mobile portrait */
		for (let i = 0; i < props.nrOfEmptyElements; i++) {
			coverElements.push(<EmptySpanWrapper platform={props.platform} key={i}></EmptySpanWrapper>);
		}

		return (
			<SkapaModal visible={props.showSheet} handleCloseBtn={(): void => onClick && onClick()}>
				<SkapaSheets
					size={'small'}
					alignment={'right'}
					header={
						<SkapaModalHeader
							title={
								props.missingCover
									? localeService.getTranslation('sprValidationCoverMissingTitle')
									: localeService.getTranslation('selectCover')
							}
						/>
					}
					footer={
						<SkapaModalFooter renderBorder>
							<SkapaButtonWrapper>
								<SkapaButton
									text={localeService.getTranslation('selectCover')}
									type={'primary'}
									onClick={(): void => onClick && onClick()}
								></SkapaButton>
							</SkapaButtonWrapper>
						</SkapaModalFooter>
					}
					style={{
						zIndex: 10,
						borderTop:
							SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
						borderBottom:
							SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
					}}
				>
					<SkapaModalBody>
						{props.missingCover ? (
							<MissingCoverWrapper>
								<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium}>
									{localeService.getTranslation('sprValidationCoverMissingDescription')}
								</KompisText>
							</MissingCoverWrapper>
						) : null}
						<CoversWrapper missingCover={props.missingCover}>
							<StyledCover>{coverElements}</StyledCover>
						</CoversWrapper>
						<StyledCoverHeader>
							<StyledNameIcon>
								<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyLarge} bold={true}>
									{props.sofaRangeName}
								</KompisText>
								<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyLarge}>
									{props.selectedCover.name}
								</KompisText>
							</StyledNameIcon>
						</StyledCoverHeader>
						<MediaWrapper onlyOneImage={false}>
							{props.kompisSettings && props.kompisTranslations && (
								<CarouselContainer>
									<KompisMediaCarousel
										translations={props.kompisTranslations.kompis}
										theme={SkapaTheme}
										mediaItems={props.images}
										skipListingLabel={localeService.getTranslation('skipImageThumbnailList')}
										showLessLabel={localeService.getTranslation('carouselShowLess')}
										selectedIndex={props.selectedIndex as number | 0}
										showArrows={props.images.length > 1}
										showScrollbar={props.images.length > 1}
										onChange={(e: CustomEvent): void =>
											onChange && onChange(e.detail.selectedIndex)
										}
										// @ts-expect-error Kompis elemenets support style but it isn't exposed
										style={{
											width: '95%',
											height: '100%',
										}}
									></KompisMediaCarousel>
								</CarouselContainer>
							)}
						</MediaWrapper>
						<CoverPickerDescriptionLightbox
							productDescriptionOpen={props.productDescriptionOpen as boolean}
							onExpand={(): void => onProductDescriptionExpand && onProductDescriptionExpand()}
						/>
					</SkapaModalBody>
				</SkapaSheets>
			</SkapaModal>
		);
	}
}
